import React, { useState } from "react";
import useStyles from "./styles";
import { DetailsTableProps } from "./types";
import { Bar } from "react-chartjs-2";
import { Grid, Switch, Paper } from "@material-ui/core";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import { ExportCsv, ExportPdf } from "@material-table/exporters";
import { useSelector } from "react-redux";
import { AppState } from "../../../../store";
import { Market } from "../../../../models/Market";
import { Currency } from "../../../../components/misc/Currency";
import { useI18n } from "../../../../hooks/useI18n";

const DetailsTable = ({
  detailsMap,
  headers,
  percentage = { showPercentage: false, total: 0, title: "" }
}: DetailsTableProps) => {
  const classes = useStyles();
  const [showGraph, setShowGraph] = useState(false);
  const { t } = useI18n();

  let market = useSelector<AppState, Market>(s => s.currentMarket) || {};

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowGraph(event.target.checked);
  };

  const getArrayFromDetailsMap = (): any[] => {
    let data: any[];
    if (detailsMap instanceof Map) {
      data = [...detailsMap.entries()];
    } else {
      data = Object.entries(detailsMap);
    }

    return data;
  };

  const populateData = (): any[] => {
    return getArrayFromDetailsMap().map(([key, value]) => {
      return {
        title: key.charAt(0).toUpperCase() + key.slice(1),
        sum: value,
        sumFormatted: <Currency value={value} />,
        percentage: percentage?.showPercentage
          ? `${((value * 100) / percentage.total).toFixed(2)}%`
          : ""
      };
    });
  };

  // @ts-ignore
  return (
    <div className={classes.container}>
      <Grid className={classes.topHeader}>
        <Grid
          component="label"
          container
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
        >
          <Grid item>{t("common.messages.table")}</Grid>
          <Grid item>
            <Switch
              size={"small"}
              checked={showGraph}
              onChange={handleChange}
              name="graphSwitch"
            />
          </Grid>
          <Grid item>{t("common.messages.chart")}</Grid>
        </Grid>
      </Grid>
      {showGraph ? (
        <Bar
          type={Bar}
          data={{
            labels: getArrayFromDetailsMap().map(
              ([key, value]) => key.charAt(0).toUpperCase() + key.slice(1)
            ),
            datasets: [
              {
                backgroundColor: "rgba(5,89,141, 0.4)",
                borderColor: "rgb(5,89,141)",
                maxBarThickness: 50,
                borderWidth: 1,
                borderRadius: 1,
                data: getArrayFromDetailsMap().map(([key, value]) => value)
              }
            ]
          }}
          options={{
            plugins: {
              legend: {
                display: false
              }
            },
            //@ts-ignore
            tooltips: {
              callbacks: {
                label: function (tooltipItem: any) {
                  return tooltipItem.yLabel;
                }
              }
            }
          }}
        />
      ) : (
        <MaterialTable
          title=""
          columns={[
            {
              title: headers[0],
              field: "title",
              cellStyle: { padding: "8px" }
            },
            {
              title: headers[1],
              field: "sumFormatted",
              cellStyle: { padding: "8px" },
              customSort: (a, b) => {
                return a.sum - b.sum;
              }
            },
            percentage?.showPercentage
              ? {
                  title: percentage.title,
                  field: "percentage",
                  cellStyle: { padding: "8px" },
                  customSort: (a, b) => {
                    let x = a.percentage.slice(0, -1);
                    let y = b.percentage.slice(0, -1);
                    return parseInt(x) - parseInt(y);
                  }
                }
              : {}
          ]}
          data={populateData()}
          options={{
            headerStyle: {
              backgroundColor: "#05598d",
              color: "#fff",
              fontWeight: "bold",
              padding: "8px"
            },
            search: false,
            paging: false,
            exportMenu: [
              {
                label: t("common.export.exportPdf"),
                exportFunc: (cols, data) =>
                  ExportPdf(cols, data, `${cols[0].title} statistics`)
              },
              {
                label: t("common.export.exportCsv"),
                exportFunc: (cols, data) =>
                  ExportCsv(cols, data, `${cols[0].title} statistics`)
              }
            ],
            exportAllData: true
          }}
          components={{
            Container: props => <Paper {...props} elevation={0} />,
            Toolbar: props => (
              <div style={{ height: "0px" }}>
                <MTableToolbar {...props} classes={{ root: classes.toolbar }} />
              </div>
            )
          }}
        />
      )}
    </div>
  );
};
export default DetailsTable;
